import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    buildColumnPreferenceKey,
    getColumnPreferences,
    resolveVisibleColumns,
    setColumnPreferences,
} from '../utils/columnPreferences';

/**
 * Manages visible table columns with localStorage persistence (30-day TTL).
 *
 * @param {object} params
 * @param {string} params.pageId - from COLUMN_PREF_PAGES
 * @param {string|number|null|undefined} params.userId
 * @param {string[]} params.allColumnKeys
 * @param {string[]} params.fixedColumnKeys - always visible, not in dropdown
 * @param {string[]} params.hiddenByDefaultKeys - used when no saved pref exists
 * @returns {{ visibleColumns: string[], handleColumnToggle: (checkedValues: string[]) => void }}
 */
export function useVisibleColumns({
    pageId,
    userId,
    allColumnKeys,
    fixedColumnKeys,
    hiddenByDefaultKeys,
}) {
    const storageKey = useMemo(
        () => buildColumnPreferenceKey(pageId, userId),
        [pageId, userId]
    );

    const fixedKeys = useMemo(() => fixedColumnKeys, [fixedColumnKeys]);
    const fixedSet = useMemo(() => new Set(fixedKeys), [fixedKeys]);
    const hiddenByDefaultSet = useMemo(
        () => new Set(hiddenByDefaultKeys),
        [hiddenByDefaultKeys]
    );

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = getColumnPreferences(storageKey);
        return resolveVisibleColumns({
            allKeys: allColumnKeys,
            fixedKeys,
            hiddenByDefaultKeys: hiddenByDefaultSet,
            savedToggleableKeys: saved,
        });
    });

    useEffect(() => {
        const saved = getColumnPreferences(storageKey);
        setVisibleColumns(
            resolveVisibleColumns({
                allKeys: allColumnKeys,
                fixedKeys,
                hiddenByDefaultKeys: hiddenByDefaultSet,
                savedToggleableKeys: saved,
            })
        );
    }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const prevAllColumnKeysRef = useRef(allColumnKeys);

    // Only auto-show columns newly added in an app update — not ones the user hid.
    useEffect(() => {
        const prevAllKeysSet = new Set(prevAllColumnKeysRef.current);
        const genuinelyNewKeys = allColumnKeys.filter(
            (k) => !prevAllKeysSet.has(k) && !fixedSet.has(k)
        );
        prevAllColumnKeysRef.current = allColumnKeys;

        if (genuinelyNewKeys.length === 0) return;

        setVisibleColumns((prev) => {
            const appended = genuinelyNewKeys.filter((k) => !hiddenByDefaultSet.has(k));
            if (appended.length === 0) return prev;
            return [...prev, ...appended];
        });
    }, [allColumnKeys, fixedSet, hiddenByDefaultSet]);

    const handleColumnToggle = useCallback(
        (checkedValues) => {
            const next = [...fixedKeys, ...checkedValues];
            setVisibleColumns(next);
            setColumnPreferences(storageKey, checkedValues);
        },
        [fixedKeys, storageKey]
    );

    return { visibleColumns, handleColumnToggle };
}
